/**
 * worldsCatalog — the one model the hub, the prerendered shells, and the sitemap
 * all read, so they cannot drift. Derived from planet/data/worlds.ts; this file
 * adds presentation and URL concerns only, never a second roster.
 */
import { REGIONS, planetKindOf, type HdDistrict } from '../planet/data/districts.ts';
import {
  WORLDS,
  isCityWorld,
  type CityWorldId,
  type WorldSpec,
  groupsForWorld,
  planetCountForWorld,
  type WorldId,
} from '../planet/data/worlds.ts';
import { DISTRICT_POSTER_IDS } from './districtPosters.ts';

/** Hong Kong's bare-route landing planet — mirrors PlanetRoute.tsx:1433. */
const HK_DEFAULT_DISTRICT = 'central-belt';

export interface WorldCard {
  readonly id: CityWorldId;
  readonly place: string;
  readonly flag: string;
  /**
   * Browse path used by the hub and every interlink. Hong Kong uses its `/hk`
   * alias so the roster reads uniformly; `/planet` stays live and canonical-free
   * as the published WorldIndexV1 deep-link target (ADR-0228). Spec D5.
   */
  readonly path: string;
  readonly planetCount: number;
  /** Concrete district a mode deep-link needs. */
  readonly heroPlanet: string;
  /** The whole-city CUBE planet — the showcase visual (posters + live embed). */
  readonly cubePlanet: string;
}

// CITY worlds only. Every consumer of WORLD_CARDS means "the cities" — NumberWall counts
// them, CityRoster groups them by continent, worldsSeo writes "N cities", the carousel
// spins them. A collection world (Sport) has no continent and no beacon, so including it
// here would make each of those statements false. It reaches players through the picker
// tab and its own route instead.
export const WORLD_CARDS: readonly WorldCard[] = WORLDS.filter(
  (w): w is WorldSpec & { readonly id: CityWorldId } => isCityWorld(w.id),
).map((w) => ({
  id: w.id,
  place: w.place,
  flag: w.flag,
  path: w.id === 'hk' ? '/hk' : w.path,
  planetCount: planetCountForWorld(w.id),
  heroPlanet: w.id === 'hk' ? HK_DEFAULT_DISTRICT : REGIONS[w.id].defaultDistrict,
  // hksar-cube, not victoria-cube: the same choice the poster capture makes —
  // the whole-territory ball, and light enough to run everywhere.
  cubePlanet: w.id === 'hk' ? 'hksar-cube' : REGIONS[w.id].wholeCity,
}));

/**
 * Where a card's Enter goes: the world's FRONT DOOR — Hong Kong's central-belt,
 * every other city's own landing belt. It used to be the whole-city cube, which
 * made the carousel the one entrance on the site that dropped you on an orbit
 * ball instead of in a city (the hero CTA and the roster both already used the
 * front door). Same planet the `?district=` in a shared /planet link names.
 */
export function enterHref(card: WorldCard): string {
  return `${card.path}?district=${card.heroPlanet}`;
}

/**
 * The planets a card can showcase: every walkable 6-face cube-belt district
 * PLUS the whole-city cube above them ('Tokyo · Whole-City Cube'). The card used
 * to be the cube and nothing else, which sold each city as one distant ball; the
 * districts are the answer to that, and the cube stays in the rotation because
 * it is the one planet that IS the city.
 *
 * Ultra street tiles are left out on purpose — HK's two are the heaviest planets
 * we ship, and a live card that stutters is worse than no live card.
 */
export function showcaseDistrictsForWorld(id: WorldId): readonly HdDistrict[] {
  return groupsForWorld(id)
    .flatMap((g) => g.members)
    .filter((d) => {
      const kind = planetKindOf(d);
      return kind === 'belt' || kind === 'territories';
    });
}

/**
 * Which district a card showcases this visit. `roll` is one 0–1 number the
 * caller draws ONCE per mount — passed in rather than rolled here so the pick is
 * a pure function (testable, and stable for the life of a card: a district that
 * re-rolled per render would remount the live embed on every frame).
 *
 * The world id is mixed in so a single roll still lands every city on a
 * different district instead of every card showing its own third belt.
 */
export function showcaseDistrict(card: WorldCard, roll: number): string {
  const pool = showcaseDistrictsForWorld(card.id);
  if (pool.length === 0) return card.cubePlanet; // a world with no belts yet
  const idx = (Math.floor(roll * 0xffff) + hashWorldId(card.id)) % pool.length;
  return pool[idx]?.id ?? card.cubePlanet;
}

/** FNV-1a over the world id — a stable per-city offset, so one shared roll
 *  spreads across the roster instead of picking the same index 24 times. */
function hashWorldId(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Deep link to one showcased planet — what the live card embeds. */
export function showcaseHref(card: WorldCard, districtId: string): string {
  return `${card.path}?district=${districtId}`;
}

/**
 * The still frame for a card. District posters only exist once
 * `capture-planet-posters.mjs --planets=…` has baked them, so an unbaked
 * district falls back to the city's own capture rather than a broken image.
 */
export function posterHref(card: WorldCard, districtId: string): string {
  const id = DISTRICT_POSTER_IDS.includes(districtId) ? districtId : card.id;
  return `/assets/planet/posters/${id}.jpg`;
}

export interface PlanetMode {
  readonly id: 'explore' | 'courier' | 'survival' | 'rally';
  readonly label: string;
  readonly blurb: string;
  /** URL `mode` value; empty means free roam (no param). */
  readonly query: string;
}

export const PLANET_MODES: readonly PlanetMode[] = [
  { id: 'explore', label: 'Explore', blurb: 'Walk, drive and ride the city', query: '' },
  { id: 'courier', label: 'Courier Rush', blurb: 'Time-attack deliveries', query: 'courier' },
  { id: 'survival', label: 'Survival', blurb: 'Hold the streets against waves', query: 'survival' },
  { id: 'rally', label: 'Drive', blurb: 'Kart + rally, one street race', query: 'drive' },
];

/** Deep link into one world in one mode. */
export function runHref(card: WorldCard, mode: PlanetMode): string {
  return districtHref(card, card.heroPlanet, mode);
}

/** A belt Explore link arrives on the street. A cube has no street to land on.
 *  Courier, Survival and Drive keep their own cameras. */
export function districtHref(card: WorldCard, district: string, mode: PlanetMode): string {
  const base = `${card.path}?district=${district}`;
  if (mode.query) return `${base}&mode=${mode.query}`;
  if (district.endsWith('-belt')) return `${base}&arrive=street`;
  return base;
}

/** Every path worth listing in sitemap.xml: the hub, each world, each planet. */
export function sitemapPaths(): string[] {
  const paths = ['/worlds'];
  for (const card of WORLD_CARDS) {
    paths.push(card.path);
    for (const group of groupsForWorld(card.id)) {
      for (const member of group.members) {
        paths.push(`${card.path}?district=${member.id}`);
      }
    }
  }
  return [...new Set(paths)];
}
